import { useEffect, useMemo } from 'react';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from '@headlessui/react';
import { useTranslations, useLocale } from 'next-intl';
import { useParams, usePathname } from 'next/navigation';
import { ListingsListHeader } from './ListHeader';
import { DisclosureIcon } from '@/component/Icons/DisclosureIcon';
import { useLazyQuery } from '@apollo/client';
import { SearchLocationGroupsQuery, DESTINATIONS_PLACEMENT } from '@/query';

import styles from './Placeholder.module.css';
import { ListComponent } from './List';
import { LocationEnum } from '@/__generated__/graphql';
import { getListingsVariables } from './api_utils';
import { getInitialSearchState } from '@/utils';
import { useRouter } from '@/i18n/routing';

export const ListingsPlaceholder = ({
  isEmpty: isListEmpty,
  variables,
  query,
  width,
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const [fetch, { loading, data }] = useLazyQuery(SearchLocationGroupsQuery, {
    variables: {
      filter: { purpose: DESTINATIONS_PLACEMENT },
      locale,
    },
    errorPolicy: 'ignore',
  });

  const locations = (data?.listingGroups?.collection || []).filter(
    (item) => query.locationId !== item?.location?.id
  );
  const hasLocations = locations?.length;
  useEffect(() => {
    fetch({
      variables: {
        filter: { locale, purpose: DESTINATIONS_PLACEMENT },
      },
    });
  }, [variables]);

  const handleLocation = (locationName) => {
    router.push({
      pathname,
      query: {
        ...params,
        locationName,
      },
    });
  };

  return loading ? null : !hasLocations ? (
    isListEmpty && (
      <div className="max-sm:mb-6 mb-12" data-testid="placeholder-no-results">
        <ListingsListHeader desc="listings.popular.description">
          {t('listings.popular.title')}
        </ListingsListHeader>
      </div>
    )
  ) : (
    <>
      <section className={styles.placeholder}>
        {isListEmpty && (
          <div className="max-sm:mb-6 mb-12">
            <ListingsListHeader desc="listings.popular.special">
              {t('listings.popular.title')}
            </ListingsListHeader>
          </div>
        )}
        <div>
          {locations.map((locationGroup) => (
            <LocationPlaceholderComponent
              key={locationGroup.name}
              locationGroup={locationGroup}
              query={query}
              width={width}
              handleLocation={handleLocation}
            />
          ))}
        </div>
      </section>
    </>
  );
};

const LocationPlaceholderComponent = ({
  width,
  query,
  locationGroup,
  handleLocation,
}) => {
  const t = useTranslations();
  const subQuery = useMemo(
    () => ({
      ...query,
      locationId: locationGroup.location?.id || '',
      locationType: locationGroup.location?.type || LocationEnum.Locality,
      locationName: locationGroup.name,
    }),
    [query]
  );
  const subSearchState = getInitialSearchState(subQuery);
  const subVariables = getListingsVariables(
    subSearchState.inexactStay,
    subSearchState.guests,
    subSearchState.location,
    subSearchState.pets,
    subSearchState.date,
    subSearchState.price,
    subSearchState.amenities,
    subSearchState.rooms,
    subSearchState.rating,
    subSearchState.tab,
  );

  return (
    <div key={locationGroup.name}>
      <ListComponent
        query={subQuery}
        variables={subVariables}
        searchState={subSearchState}
        width={width}
      >
        {({ list, totalCount, isEmpty }) =>
          isEmpty ? null : (
            <Disclosure>
              <DisclosureButton className="flex w-full justify-between items-center py-[18px] lg:py-8 max-sm:pr-3 max-lg:pr-4 lg:pr-9 border-t border-slate-100 text-left text-sm font-medium">
                <span className="text-slate-700 text-base lg:text-2xl font-medium">
                  {totalCount} {t('listings.alternatives.title')}{' '}
                  <span
                    className="text-teal-600 cursor-pointer"
                    onClick={() => handleLocation(locationGroup.name)}
                  >
                    {t('listings.result.title-after')} {locationGroup.name}
                  </span>{' '}
                  {t('listings.alternatives.title-after')}
                </span>
                <DisclosureIcon className="text-slate-400 data-[open]:rotate-45 data-[open]:transform transition ease-linear delay-100" />
              </DisclosureButton>
              <Transition
                as="div"
                enter="transition-all duration-200 ease-linear"
                enterFrom="transform scale-0 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition-all duration-200 ease-linear"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-0 opacity-0"
              >
                <DisclosurePanel className="sm:max-list-2xl:-mx-3">
                  {list}
                </DisclosurePanel>
              </Transition>
            </Disclosure>
          )
        }
      </ListComponent>
    </div>
  );
};
